import { localCacheDB, DashboardCacheRecord, DashboardCachedPayload } from '../../db/localCache';
import type { DashboardResponse } from './api';
import type { AdvancedDashboardData } from './dashboardAdvancedTypes';
import type { DashboardViewMode } from './DashboardContext';
import { emptyAdvancedData } from './dashboardAdvancedTypes';

export const buildDashboardCacheKey = (
  from: string,
  to: string,
  mode: DashboardViewMode,
  userId?: number | string | null,
): string => {
  const cacheMode = mode === 'advanced' ? 'advanced' : mode === 'users' ? 'users' : 'simple';
  // Figures are filtered by the viewer's data scope, so they are cached per user.
  const id = Number(userId);
  const owner = Number.isFinite(id) && id > 0 ? `:u${id}` : '';
  return `dashboard:${from}:${to}:${cacheMode}${owner}`;
};

/** Keys added after the first dashboard cache rollout — old entries must be refreshed. */
const REQUIRED_BREAKDOWN_KEYS = [
  'salesByFamily',
  'salesByCategory',
  'salesByDepot',
  'paymentsByUser',
  'paymentsByDepot',
  'supplierPaymentModes',
  'topSupplierBalances',
  'topWallets',
  'walletByTag',
] as const;

export const isLegacyDashboardCache = (payload: DashboardCachedPayload): boolean =>
  REQUIRED_BREAKDOWN_KEYS.some((key) => !(key in payload.base));

const fallbackFingerprint = (text: string): string => {
  let hash = 0;
  for (let i = 0; i < text.length; i++) {
    hash = ((hash << 5) - hash + text.charCodeAt(i)) | 0;
  }
  return `fb_${(hash >>> 0).toString(16)}`;
};

const buildFingerprintSignature = (
  payload: DashboardCachedPayload,
  mode: DashboardViewMode,
) => {
  const base = payload.base;
  return {
    kpi: base.kpi,
    counts: {
      salesTrend: base.salesTrend?.length ?? 0,
      salesProfitBreakdown: base.salesProfitBreakdown?.length ?? 0,
      projectedProfitBreakdown: base.projectedProfitBreakdown?.length ?? 0,
      paymentModes: base.paymentModes?.length ?? 0,
      salesByFamily: base.salesByFamily?.length ?? 0,
      salesByCategory: base.salesByCategory?.length ?? 0,
      salesByDepot: base.salesByDepot?.length ?? 0,
      paymentsByUser: base.paymentsByUser?.length ?? 0,
      paymentsByDepot: base.paymentsByDepot?.length ?? 0,
      topDebtors: base.topDebtors?.length ?? 0,
      topAgents: base.topAgents?.length ?? 0,
      inventoryByDepot: base.inventoryByDepot?.length ?? 0,
      recentTransactions: base.recentTransactions?.length ?? 0,
      topItems: base.topItems?.length ?? 0,
      creditRiskClients: base.creditRiskClients?.length ?? 0,
      topWallets: base.topWallets?.length ?? 0,
      walletByTag: base.walletByTag?.length ?? 0,
    },
    trendTail: (base.salesTrend ?? []).slice(-5).map((p) => `${p.date}:${p.amount}:${p.count}`),
    recentHead: (base.recentTransactions ?? [])
      .slice(0, 5)
      .map((t) => `${t.type}:${t.id}:${t.date}:${t.amount}`),
    advanced:
      mode === 'advanced' && payload.advanced
        ? {
            kpi: payload.advanced.kpi,
            kpiDeltas: payload.advanced.kpiDeltas,
            alertIds: (payload.advanced.alerts ?? []).map((a) => a.id).sort(),
            dualTrendLen: payload.advanced.dualTrend?.length ?? 0,
          }
        : null,
  };
};

/** Compact content signature — compares KPIs + shape, not full JSON (fast, no extra API). */
export const buildDashboardFingerprint = async (
  payload: DashboardCachedPayload,
  mode: DashboardViewMode,
): Promise<string> => {
  const json = JSON.stringify(buildFingerprintSignature(payload, mode));

  if (typeof crypto !== 'undefined' && crypto.subtle?.digest) {
    try {
      const bytes = new TextEncoder().encode(json);
      const digest = await crypto.subtle.digest('SHA-256', bytes);
      return Array.from(new Uint8Array(digest))
        .map((b) => b.toString(16).padStart(2, '0'))
        .join('');
    } catch (error) {
      console.warn('Dashboard fingerprint SHA-256 failed, using fallback hash:', error);
    }
  }

  return fallbackFingerprint(json);
};

const ensureDashboardCacheReady = async (): Promise<boolean> => {
  try {
    await localCacheDB.open();
    return localCacheDB.isOpen();
  } catch (error) {
    console.error('Dashboard cache DB failed to open:', error);
    return false;
  }
};

export const parseDashboardResponse = (
  data: DashboardResponse,
  wantAdvanced: boolean,
): DashboardCachedPayload => {
  const { advanced, ...base } = data;
  return {
    base: { ...base, advanced: undefined },
    advanced:
      wantAdvanced && advanced
        ? { ...emptyAdvancedData, ...advanced, kpi: { ...emptyAdvancedData.kpi, ...advanced.kpi } }
        : undefined,
  };
};

export const getDashboardCache = async (
  cacheKey: string,
): Promise<DashboardCacheRecord | undefined> => {
  try {
    if (!(await ensureDashboardCacheReady())) return undefined;
    return await localCacheDB.dashboardEntries.get(cacheKey);
  } catch (error) {
    console.error('Dashboard cache read failed:', error);
    return undefined;
  }
};

export const saveDashboardCache = async (
  entry: Omit<DashboardCacheRecord, 'fetchedAt'> & { fetchedAt?: string },
): Promise<boolean> => {
  try {
    if (!(await ensureDashboardCacheReady())) return false;

    const record: DashboardCacheRecord = {
      ...entry,
      fetchedAt: entry.fetchedAt ?? new Date().toISOString(),
    };

    await localCacheDB.dashboardEntries.put(record);

    const verify = await localCacheDB.dashboardEntries.get(entry.cacheKey);
    if (!verify?.payload) {
      console.error('Dashboard cache write verification failed for key:', entry.cacheKey);
      return false;
    }

    return true;
  } catch (error) {
    console.error('Dashboard cache write failed:', error);
    return false;
  }
};